import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Long-form Thai guide: company documents certified for use abroad. */
const TITLE = "เอกสารบริษัทไปใช้ต่างประเทศ — รับรองอย่างไรให้ปลายทางรับ";
const DESC =
  "หนังสือรับรองนิติบุคคล บัญชีรายชื่อผู้ถือหุ้น งบการเงิน และมติที่ประชุม ต้องรับรองชั้นใดบ้างก่อนใช้ต่างประเทศ พร้อมลำดับขั้นตอนและข้อผิดพลาดที่พบบ่อย";
const URL = "/blog/company-documents-for-abroad";
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "เอกสารบริษัทที่ต้องใช้ต่างประเทศมีอะไรบ้าง",
    answer:
      "ชุดที่พบบ่อยคือหนังสือรับรองนิติบุคคล บัญชีรายชื่อผู้ถือหุ้น (บอจ.5) หนังสือรับรองตราสำคัญของบริษัท งบการเงินที่ยื่นแล้ว มติที่ประชุมกรรมการหรือผู้ถือหุ้น และหนังสือมอบอำนาจ รายการจริงขึ้นกับวัตถุประสงค์ เช่น การเปิดบัญชีธนาคาร การจัดตั้งบริษัทลูก หรือการเข้าประมูลงาน",
  },
  {
    question: "ต้องคัดเอกสารใหม่ทุกครั้งหรือไม่",
    answer:
      "ปลายทางส่วนใหญ่กำหนดว่าหนังสือรับรองนิติบุคคลต้องคัดใหม่ไม่เกินระยะเวลาหนึ่งก่อนวันใช้งาน จึงมักต้องคัดใหม่แม้จะมีฉบับเดิมอยู่ ควรตรวจกรอบเวลาที่ปลายทางกำหนดก่อนคัด เพื่อไม่ให้เอกสารเก่าเกินกำหนดในวันยื่น",
  },
  {
    question: "ต้องแปลเป็นภาษาอังกฤษหรือภาษาปลายทาง",
    answer:
      "ขึ้นกับหน่วยงานผู้รับ บางประเทศรับคำแปลภาษาอังกฤษ บางประเทศกำหนดให้แปลเป็นภาษาราชการของตนและต้องรับรองในประเทศนั้น การถามให้ชัดก่อนเริ่มแปลช่วยลดค่าใช้จ่ายที่เกิดจากการแปลผิดภาษา",
  },
  {
    question: "ลำดับการรับรองเป็นอย่างไร",
    answer:
      "แนวทางที่พบบ่อยคือใช้เอกสารที่ออกหรือรับรองโดยหน่วยงานทะเบียน จัดทำคำแปลตามรูปแบบที่กรมการกงสุลรับ ยื่นรับรองนิติกรณ์ แล้วรับรองที่สถานทูตหรือสถานกงสุลปลายทางเมื่อจำเป็น สำหรับประเทศที่ใช้ระบบ Apostille ให้ตรวจว่าเอกสารประเภทนั้นเข้าเกณฑ์หรือไม่",
  },
  {
    question: "มติที่ประชุมต้องรับรองอย่างไร",
    answer:
      "โดยทั่วไปกรรมการผู้มีอำนาจลงนามรับรองสำเนาถูกต้องและลงนามต่อหน้าผู้มีอำนาจรับรองลายมือชื่อ จากนั้นจึงเข้าสู่ชั้นนิติกรณ์ ควรแนบหนังสือรับรองนิติบุคคลไปด้วยเพื่อให้ปลายทางตรวจอำนาจผู้ลงนามได้",
  },
  {
    question: "งบการเงินต้องใช้ฉบับใด",
    answer:
      "ควรใช้ฉบับที่ยื่นต่อหน่วยงานทะเบียนแล้วและมีหลักฐานการยื่น ไม่ใช่ไฟล์ร่างภายใน เพราะปลายทางต้องเห็นว่าเป็นชุดที่ผ่านการยื่นตามกฎหมาย และหลายกรณีต้องมีลายมือชื่อผู้สอบบัญชีปรากฏชัด",
  },
  {
    question: "ถ้าปลายทางเป็นธนาคารต่างประเทศต้องเตรียมอะไรเพิ่ม",
    answer:
      "ธนาคารมักขอเอกสารพิสูจน์โครงสร้างการถือหุ้นและผู้รับผลประโยชน์ที่แท้จริง รวมถึงเอกสารแสดงตนของกรรมการ ควรถามรายการเอกสารเป็นลายลักษณ์อักษรจากธนาคารก่อน แล้วจึงวางลำดับการรับรองทั้งชุดในคราวเดียว",
  },
];

export const Route = createFileRoute("/blog/company-documents-for-abroad")({
  component: CompanyDocsAbroad,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("company-documents-for-abroad", "th") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "รับรองเอกสารบริษัทไปต่างประเทศ, แปลหนังสือรับรองบริษัท, บอจ.5 แปลอังกฤษ, รับรองมติที่ประชุมกรรมการ, นิติกรณ์เอกสารบริษัท",
        },
      ],
      links: canonicalAndHreflang(URL),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "บทความ", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("company-documents-for-abroad"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Corporate",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ name: string; note: string }> = [
  {
    name: "หนังสือรับรองนิติบุคคล",
    note: "คัดใหม่ให้อยู่ในกรอบเวลาที่ปลายทางกำหนด ตรวจว่ารายชื่อกรรมการและอำนาจลงนามเป็นปัจจุบัน",
  },
  {
    name: "บัญชีรายชื่อผู้ถือหุ้น",
    note: "ใช้ฉบับที่ยื่นต่อหน่วยงานทะเบียนแล้ว และตรวจว่าสัดส่วนการถือหุ้นตรงกับที่แจ้งปลายทาง",
  },
  {
    name: "หนังสือรับรองตราสำคัญ",
    note: "จำเป็นเมื่อปลายทางต้องเทียบตราบนเอกสารที่ลงนาม ควรเตรียมไปพร้อมชุดหลัก",
  },
  {
    name: "งบการเงิน",
    note: "ใช้ชุดที่ยื่นแล้วพร้อมหลักฐานการยื่น ตรวจว่าลายมือชื่อผู้สอบบัญชีปรากฏชัดเจนบนสำเนา",
  },
  {
    name: "มติที่ประชุมกรรมการหรือผู้ถือหุ้น",
    note: "ถ้อยคำต้องระบุการอนุมัติเรื่องที่จะไปใช้ปลายทางอย่างเฉพาะเจาะจง ไม่ใช่ข้อความกว้าง",
  },
  {
    name: "หนังสือมอบอำนาจ",
    note: "ระบุขั้นตอนที่ผู้แทนต้องทำเป็นรายข้อ และแนบหนังสือรับรองนิติบุคคลให้ปลายทางตรวจอำนาจ",
  },
];

function CompanyDocsAbroad() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/" className="text-muted-foreground hover:text-foreground">
          หน้าแรก
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          บทความ
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="company-documents-for-abroad" locale="th" />
      <p className="answer-summary speakable-summary">
        เอกสารบริษัทที่ส่งไปใช้ต่างประเทศถูกตีกลับบ่อยกว่าเอกสารบุคคล เพราะมีหลายฉบับในชุดเดียวกัน
        และปลายทางต้องตรวจ “อำนาจผู้ลงนาม” ควบคู่กับเนื้อหาเอกสาร บทความนี้เรียบเรียงรายการเอกสาร
        ลำดับการรับรอง และจุดตรวจก่อนส่ง เพื่อให้ทั้งชุดผ่านในรอบเดียว
      </p>

      <h2>1. หลักคิด — ปลายทางตรวจสองเรื่องพร้อมกัน</h2>
      <p>
        เรื่องแรกคือเนื้อหาเอกสารว่าตรงกับที่แจ้งไว้หรือไม่ เรื่องที่สองคือผู้ลงนามมีอำนาจตามทะเบียนจริงหรือไม่
        ด้วยเหตุนี้เอกสารที่ลงนามโดยกรรมการจึงมักต้องแนบหนังสือรับรองนิติบุคคลฉบับปัจจุบันไปด้วยเสมอ
        การส่งเฉพาะเอกสารที่ปลายทางร้องขอโดยไม่แนบหลักฐานอำนาจ เป็นสาเหตุการตีกลับที่พบมากที่สุดในกลุ่มนี้
      </p>

      <h2>2. รายการเอกสารและจุดตรวจของแต่ละฉบับ</h2>
      <div className="not-prose overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">เอกสาร</th>
              <th className="p-2 text-left">จุดตรวจ</th>
            </tr>
          </thead>
          <tbody>
            {DOCS.map((d) => (
              <tr key={d.name} className="border-b align-top">
                <td className="p-2 font-medium">{d.name}</td>
                <td className="p-2">{d.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>3. ลำดับการทำงานที่ลดรอบแก้ไข</h2>
      <ol>
        <li>ขอรายการเอกสารจากปลายทางเป็นลายลักษณ์อักษร ระบุภาษาและกรอบอายุเอกสาร</li>
        <li>คัดเอกสารทะเบียนใหม่ให้อยู่ในกรอบเวลา และตรวจอำนาจลงนามให้เป็นปัจจุบัน</li>
        <li>ร่างมติที่ประชุมและหนังสือมอบอำนาจให้ถ้อยคำครอบคลุมทุกขั้นตอนที่ต้องทำ</li>
        <li>จัดทำคำแปลทั้งชุดในคราวเดียว เพื่อให้ชื่อบุคคล ชื่อบริษัท และคำศัพท์สอดคล้องกันทุกฉบับ</li>
        <li>ลงนามต่อหน้าผู้มีอำนาจรับรองลายมือชื่อ แล้วยื่นนิติกรณ์และรับรองสถานทูตตามลำดับ</li>
        <li>ตรวจชุดสุดท้ายทีละหน้าก่อนส่งออก และเก็บสำเนาทุกชั้นการรับรองไว้อ้างอิง</li>
      </ol>

      <h2>4. ข้อผิดพลาดที่ทำให้ทั้งชุดถูกตีกลับ</h2>
      <ul>
        <li>แปลชื่อบริษัทไม่ตรงกันระหว่างฉบับ ทำให้ปลายทางมองว่าเป็นนิติบุคคลต่างราย</li>
        <li>ใช้หนังสือรับรองที่คัดไว้นานเกินกรอบเวลาของปลายทาง</li>
        <li>มติที่ประชุมใช้ถ้อยคำกว้าง ไม่ระบุเรื่องที่อนุมัติอย่างเฉพาะเจาะจง</li>
        <li>ไม่แนบหลักฐานอำนาจผู้ลงนาม ทำให้ปลายทางไม่สามารถตรวจได้</li>
        <li>แปลไม่ครบข้อความบนตราประทับและข้อความท้ายเอกสาร</li>
        <li>แยกส่งทีละฉบับหลายรอบ ทำให้คำศัพท์และการสะกดไม่สอดคล้องกัน</li>
      </ul>

      <h2>5. คำถามที่พบบ่อย</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>6. แหล่งอ้างอิงและบริการที่เกี่ยวข้อง</h2>
      <p className="text-sm">
        แหล่งอ้างอิง:{" "}
        <a href="https://www.dbd.go.th/" target="_blank" rel="noreferrer">
          กรมพัฒนาธุรกิจการค้า
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/th/publicservice/legalization" target="_blank" rel="noreferrer">
          กรมการกงสุล กระทรวงการต่างประเทศ
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/blog/power-of-attorney-abroad-guide" className="text-primary underline">
            หนังสือมอบอำนาจไปใช้ต่างประเทศ — เขียนอย่างไรไม่ให้ถูกตีกลับ
          </Link>
        </li>
        <li>
          <Link to="/blog/apostille-vs-legalization-by-country" className="text-primary underline">
            Apostille กับ Legalization ต่างกันอย่างไร — เทียบรายประเทศ
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        ตรวจทานเนื้อหา ณ วันที่ {PUBLISHED} เนื้อหานี้เป็นข้อมูลทั่วไปเพื่อวางลำดับงาน
        ไม่ใช่คำวินิจฉัยแทนหน่วยงานเจ้าของเรื่อง กรุณายืนยันรายการเอกสารกับหน่วยงานปลายทางก่อนดำเนินการ
      </p>
    </article>
  );
}
